import {
    getHomeRouteForLoggedInUser,
    getUserData,
    isUserLoggedIn,
} from '@/auth/utils'
import { SUBJECT } from '@const/acl'
import { API_ENDPOINT } from '@const/api'
import { ROUTES } from '@const/routes'
import { AuthType } from '@libs/acl/ability'
import { canNavigate } from '@libs/acl/routeProtection'
import axiosIns from '@libs/axios'
import { setTokenExpiredValue } from '@util/localStorage'
import { destroySession } from '@util/userData'
import Vue from 'vue'
import VueRouter from 'vue-router'

Vue.use(VueRouter)

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior() {
        return { x: 0, y: 0 }
    },
    routes: [
        {
            path: ROUTES.HOME.path,
            name: ROUTES.HOME.name,
            component: () => import('@/views/Home.vue'),
            meta: {
                resource: new AuthType(SUBJECT.ALL),
            },
        },
        {
            path: ROUTES.INVITE.path,
            name: ROUTES.INVITE.name,
            component: () => import('@/views/user/Invite.vue'),
            meta: {
                resource: new AuthType(SUBJECT.MANAGEMENT),
                pageTitle: ROUTES.INVITE.title,
                breadcrumb: [
                    {
                        text: ROUTES.INVITE.title,
                        active: true,
                    },
                ],
            },
        },
        {
            path: ROUTES.FORGOT_PASSWORD.path,
            name: ROUTES.FORGOT_PASSWORD.name,
            component: () => import('@/views/ForgotPassword.vue'),
            meta: {
                layout: 'full',
                resource: SUBJECT.NON_AUTH,
                pageTitle: ROUTES.FORGOT_PASSWORD.title,
            },
        },
        {
            path: ROUTES.RESET_PASSWORD.path,
            name: ROUTES.RESET_PASSWORD.name,
            component: () => import('@/views/ResetPassword.vue'),
            meta: {
                layout: 'full',
                resource: SUBJECT.NON_AUTH,
                pageTitle: ROUTES.RESET_PASSWORD.title,
            },
        },
        {
            path: ROUTES.ACCOUNT_SETTINGS.path,
            name: ROUTES.ACCOUNT_SETTINGS.name,
            component: () => import('@/views/account/AccountSettings.vue'),
            meta: {
                pageTitle: ROUTES.ACCOUNT_SETTINGS.title,
                resource: new AuthType(SUBJECT.ALL),
            },
        },
        {
            path: ROUTES.USER_SETTINGS.path,
            name: ROUTES.USER_SETTINGS.name,
            component: () => import('@/views/user/UserSettings.vue'),
            meta: {
                pageTitle: ROUTES.USER_SETTINGS.title,
                resource: new AuthType(SUBJECT.ALL),
                breadcrumb: [
                    {
                        text: ROUTES.LIST.title,
                        to: ROUTES.LIST.path,
                    },
                    {
                        text: ROUTES.USER_SETTINGS.title,
                        active: true,
                    },
                ],
            },
        },
        {
            path: ROUTES.LOGIN.path,
            name: ROUTES.LOGIN.name,
            component: () => import('@/views/Login.vue'),
            meta: {
                resource: SUBJECT.NON_AUTH,
                layout: 'full',
                redirectIfLoggedIn: true,
            },
        },
        {
            path: ROUTES.LOGOUT.path,
            name: ROUTES.LOGOUT.name,
            component: {
                template: '<div></div>',
                mounted() {
                    this.$bvModal
                        .msgBoxConfirm('ログアウトの確認', {
                            cancelVariant: 'outline-secondary',
                        })
                        .then((isOK) => {
                            if (isOK) {
                                destroySession()
                                this.$bvToast.toast(`ログアウトしました`, {
                                    title: 'ログアウト成功',
                                    variant: 'success',
                                    toaster: 'b-toaster-bottom-full',
                                    solid: true,
                                })
                                this.$router.replace({
                                    name: ROUTES.LOGIN.name,
                                })
                            }
                        })
                },
            },
            meta: {
                resource: new AuthType(SUBJECT.ALL),
            },
        },
        {
            path: ROUTES.REGISTER.path,
            name: ROUTES.REGISTER.name,
            component: () => import('@/views/user/Register.vue'),
            meta: {
                resource: SUBJECT.NON_AUTH,
                layout: 'full',
                pageTitle: ROUTES.REGISTER.title,
                breadcrumb: [
                    {
                        text: ROUTES.REGISTER.title,
                        active: true,
                    },
                ],
            },
        },
        {
            path: ROUTES.FREE_COMPASS.path,
            name: ROUTES.FREE_COMPASS.name,
            component: () => import('@/views/FreeCompass.vue'),
            meta: {
                resource: SUBJECT.NON_AUTH,
                layout: 'full',
                pageTitle: ROUTES.FREE_COMPASS.title,
                breadcrumb: [
                    {
                        text: ROUTES.FREE_COMPASS.title,
                        active: true,
                    },
                ],
            },
        },
        {
            path: ROUTES.PUBLIC_LIGHT_COMPASS.path,
            name: ROUTES.PUBLIC_LIGHT_COMPASS.name,
            component: () => import('@/views/public/LightCompass.vue'),
            meta: {
                resource: SUBJECT.NON_AUTH,
                layout: 'full',
                standalonePublic: true,
                documentTitle: 'ライトコンパス',
            },
        },
        {
            path: ROUTES.PUBLIC_PASSPORT.path,
            name: ROUTES.PUBLIC_PASSPORT.name,
            // 公開URLは工事中の1ページだけを出す。作成途中の画面は /passport2026/preview。
            component: () => import('@/views/public/PassportComingSoon.vue'),
            meta: {
                resource: SUBJECT.NON_AUTH,
                layout: 'full',
                standalonePublic: true,
                documentTitle: 'パスポート',
            },
        },
        {
            path: ROUTES.PUBLIC_PASSPORT_PREVIEW.path,
            name: ROUTES.PUBLIC_PASSPORT_PREVIEW.name,
            component: () => import('@/views/public/Passport.vue'),
            meta: {
                resource: SUBJECT.NON_AUTH,
                layout: 'full',
                standalonePublic: true,
                documentTitle: 'パスポート',
            },
        },
        {
            path: ROUTES.PUBLIC_PASSPORT_PRINT.path,
            name: ROUTES.PUBLIC_PASSPORT_PRINT.name,
            component: () => import('@/views/public/PublicPassportPrint.vue'),
            meta: {
                resource: SUBJECT.NON_AUTH,
                layout: 'full',
                standalonePublic: true,
                documentTitle: 'パスポート PDF',
            },
        },
        {
            path: ROUTES.COMPASS.path,
            name: ROUTES.COMPASS.name,
            component: () => import('@/views/Compass.vue'),
            meta: {
                layout: 'full',
                resource: new AuthType(SUBJECT.COMPASS),
                pageTitle: ROUTES.COMPASS.title,
                breadcrumb: [
                    {
                        text: ROUTES.COMPASS.title,
                        active: true,
                    },
                ],
            },
        },
        {
            path: ROUTES.MASTERCHART.path,
            name: ROUTES.MASTERCHART.name,
            component: () => import('@/views/MasterChart.vue'),
            meta: {
                layout: 'full',
                resource: new AuthType(SUBJECT.MASTERCHART),
                pageTitle: ROUTES.MASTERCHART.title,
                breadcrumb: [
                    {
                        text: ROUTES.MASTERCHART.title,
                        active: true,
                    },
                ],
            },
        },
        {
            path: ROUTES.PASSPORT.path,
            name: ROUTES.PASSPORT.name,
            component: () => import('@/views/Passport.vue'),
            meta: {
                layout: 'full',
                resource: new AuthType(SUBJECT.PASSPORT),
                pageTitle: ROUTES.PASSPORT.title,
                breadcrumb: [
                    {
                        text: ROUTES.PASSPORT.title,
                        active: true,
                    },
                ],
            },
        },
        {
            path: ROUTES.YEAR_FORTUNE.path,
            name: ROUTES.YEAR_FORTUNE.name,
            component: () => import('@/views/YearFortune.vue'),
            meta: {
                layout: 'full',
                resource: new AuthType(SUBJECT.FORTUNE),
                pageTitle: ROUTES.YEAR_FORTUNE.title,
                breadcrumb: [
                    {
                        text: ROUTES.YEAR_FORTUNE.title,
                        active: true,
                    },
                ],
            },
        },
        {
            path: ROUTES.LIST.path,
            name: ROUTES.LIST.name,
            component: () => import('@/views/user/List.vue'),
            meta: {
                resource: new AuthType(SUBJECT.MANAGEMENT),
                pageTitle: ROUTES.LIST.title,
                breadcrumb: [
                    {
                        text: ROUTES.LIST.title,
                        active: true,
                    },
                ],
            },
        },
        {
            path: ROUTES.NON_AUTH.path,
            name: ROUTES.NON_AUTH.name,
            component: () => import('@/views/NotAuthorized.vue'),
            meta: {
                resource: SUBJECT.NON_AUTH,
                layout: 'full',
            },
        },
        {
            path: ROUTES.ERROR.path,
            name: ROUTES.ERROR.name,
            component: () => import('@/views/error/Error404.vue'),
            meta: {
                resource: SUBJECT.NON_AUTH,
                layout: 'full',
            },
        },
        {
            path: '*',
            redirect: ROUTES.ERROR.path,
        },
    ],
})

async function isValidToken() {
    return await axiosIns
        .post(API_ENDPOINT.AUTH_USER)
        .then((response) => {
            return true
        })
        .catch((err) => {
            destroySession()
            setTokenExpiredValue()
            return false
        })
}

router.beforeEach(async (to, from, next) => {
    if (to.meta && to.meta.standalonePublic) {
        document.title =
            typeof to.meta.documentTitle === 'string'
                ? to.meta.documentTitle
                : ''
        // 公開ページはログイン状態やJWT検証の成否に関係なく表示する。
        return next()
    }

    const isLoggedIn = isUserLoggedIn()
    const canNav = canNavigate(to)
    //console.log(`${to.name} : ${canNav}`);
    if (!canNav) {
        // ログインしていない場合はログイン画面にリダイレクト
        if (!isLoggedIn) return next({ name: ROUTES.LOGIN.name })
        //　ログインしていたら権限がない画面へ遷移
        return next({ name: ROUTES.NON_AUTH.name })
    }

    // ログイン中でjwtトークンの有効期限が切れた場合、ログイン画面に遷移
    if (isLoggedIn && !(await isValidToken()) && to.name !== ROUTES.LOGIN.name)
        return next({ name: ROUTES.LOGIN.name })

    // Redirect if logged in
    if (to.meta.redirectIfLoggedIn && isLoggedIn) {
        const userData = getUserData()
        next(getHomeRouteForLoggedInUser(userData ? userData.role : null))
    }

    return next()
})

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(() => {
    // Remove initial loading
    const appLoading = document.getElementById('loading-bg')
    if (appLoading) {
        appLoading.style.display = 'none'
    }
})

export default router
